<script>
  import { onMount, onDestroy } from 'svelte';
  import { toasts } from "svelte-toasts";
  import { getParentProducts, getChildProducts, saveAnonymousOrder, getAllProducts, getCategories, getChildProductsWithDetails, testGalleryAccess } from '../controllers/anonymousCheckoutController.js';
  import { EMAIL_API_URL } from '../config.js';

  // Accept location prop to avoid Svelte warning
  export let location = null;

  // Form data
  let formData = {
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerNIT: '',
    selectedParent: null,
    selectedChildren: [],
    totalPrice: 0,
    notes: ''
  };

  // Product data
  let parentProducts = [];
  let childProducts = [];
  let categories = [];
  let selectedCategory = null;
  let filteredProducts = [];
  let loading = false;
  let submitting = false;
  let selectedParentData = null;
  let showCart = false;
  let currentStep = 1; // 1: Customer Info, 2: Server Selection, 3: Components, 4: Review, 5: Success
  let orderSubmitted = false;
  let submittedOrder = null;

  // Set Nonosky brand colors - black background with red accents
  onMount(() => {
    document.body.style.background = '#000000';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundRepeat = 'no-repeat';
    
    loadParentProducts();
  });

  onDestroy(() => {
    document.body.style.background = '';
    document.body.style.backgroundSize = '';
    document.body.style.backgroundPosition = '';
    document.body.style.backgroundAttachment = '';
    document.body.style.backgroundRepeat = '';
  });

  // Load parent products (servers)
  async function loadParentProducts() {
    loading = true;
    try {
      console.log('Loading parent products...');
      parentProducts = await getParentProducts();
      console.log('Loaded parent products:', parentProducts);
      
      if (parentProducts.length === 0) {
        toasts.add({
          title: 'Info',
          description: 'No se encontraron servidores disponibles. Verifique que existan productos sin parent.',
          duration: 5000,
          placement: 'bottom-right',
          type: 'info',
          theme: 'dark',
          showProgress: true,
        });
      }
    } catch (error) {
      console.error('Error loading parent products:', error);
      toasts.add({
        title: 'Error',
        description: 'No se pudieron cargar los productos servidor',
        duration: 3000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
    } finally {
      loading = false;
    }
  }

  // Load child products when parent is selected
  async function loadChildProducts(parentId) {
    if (!parentId) {
      childProducts = [];
      categories = [];
      filteredProducts = [];
      selectedCategory = null;
      formData.selectedChildren = [];
      formData.totalPrice = 0;
      return;
    }

    loading = true;
    try {
      // Load products with images and categories
      childProducts = await getChildProductsWithDetails(parentId);
      
      // Load categories
      categories = await getCategories();
      
      // Set all products as default filtered
      filteredProducts = childProducts;
      
      console.log('🎯 Loaded child products:', childProducts.length);
      console.log('🏷️ Loaded categories:', categories.length);
      console.log('📦 Filtered products:', filteredProducts.length);
      
      // If no products are loaded, try alternative approach
      if (childProducts.length === 0) {
        console.log('⚠️ No child products found, trying alternative approach...');
        // Try getting products without category expansion
        const simpleProducts = await getChildProducts(parentId);
        childProducts = simpleProducts;
        filteredProducts = simpleProducts;
        console.log('🔄 Alternative products loaded:', childProducts.length);
      }
      
      // Ensure we always have products to show
      if (childProducts.length === 0) {
        console.log('❌ Still no products found, this might be a data issue');
        toasts.add({
          title: 'Info',
          description: 'No se encontraron componentes para este servidor. Verifique la configuración de productos.',
          duration: 5000,
          placement: 'bottom-right',
          type: 'info',
          theme: 'dark',
          showProgress: true,
        });
      } else {
        console.log('🎉 Products loaded successfully:', childProducts.length);
      }
    } catch (error) {
      console.error('Error loading child products:', error);
      toasts.add({
        title: 'Error',
        description: 'No se pudieron cargar los componentes',
        duration: 3000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
    } finally {
      loading = false;
    }
  }

  // Filter products by category
  function filterByCategory(categoryId) {
    selectedCategory = categoryId;
    
    console.log('🔍 Filtering by category:', categoryId);
    console.log('📦 Available child products:', childProducts.length);
    
    if (!categoryId) {
      // Show all products when "Todos" is selected
      filteredProducts = childProducts;
      console.log('✅ Showing all products:', filteredProducts.length);
    } else {
      // Filter products by category
      filteredProducts = childProducts.filter(product => {
        // Check if product has this category through the categoria_producto relationship
        const hasCategory = product.expand && 
               product.expand['categoria_producto'] && 
               Array.isArray(product.expand['categoria_producto']) &&
               product.expand['categoria_producto'].some(cp => 
                 cp.expand && cp.expand.categoria && cp.expand.categoria.id === categoryId
               );
        
        console.log(`🔍 Product ${product.nombre} has category ${categoryId}:`, hasCategory);
        if (hasCategory) {
          console.log(`✅ Product ${product.nombre} matches category ${categoryId}`);
        }
        return hasCategory;
      });
      
      // If no products found through relationship, try name-based filtering as fallback
      if (filteredProducts.length === 0) {
        console.log('⚠️ No products found through category relationship, trying name-based filtering...');
        
        // Get the category name for fallback filtering
        const selectedCategoryData = categories.find(cat => cat.id === categoryId);
        if (selectedCategoryData) {
          console.log('🔍 Trying name-based filtering for category:', selectedCategoryData.descrip);
          
          filteredProducts = childProducts.filter(product => {
            const productName = product.nombre.toLowerCase();
            const categoryName = selectedCategoryData.descrip.toLowerCase();
            
            // Simple keyword matching
            let matches = false;
            
            if (categoryName.includes('memoria') || categoryName.includes('ram')) {
              matches = productName.includes('ddr') || productName.includes('ram') || productName.includes('memoria');
            } else if (categoryName.includes('almacenamiento') || categoryName.includes('storage')) {
              matches = productName.includes('ssd') || productName.includes('hdd') || productName.includes('almacenamiento');
            } else if (categoryName.includes('cpu') || categoryName.includes('procesador')) {
              matches = productName.includes('xeon') || productName.includes('cpu') || productName.includes('procesador');
            } else if (categoryName.includes('red') || categoryName.includes('network')) {
              matches = productName.includes('puerto') || productName.includes('rj45') || productName.includes('sfp') || productName.includes('red');
            }
            
            console.log(`🔍 Product ${product.nombre} matches ${categoryName} by name:`, matches);
            return matches;
          });
          
          console.log('✅ Name-based filtered products:', filteredProducts.length);
        }
      }
      
      console.log('✅ Final filtered products by category:', filteredProducts.length);
    }
  }

  // Handle parent selection
  function selectParent(parent) {
    formData.selectedParent = parent.id;
    selectedParentData = parent;
    formData.selectedChildren = [];
    formData.totalPrice = 0;
    currentStep = 3;
    loadChildProducts(parent.id);
  }

  // Navigate between steps
  function nextStep() {
    if (currentStep < 4) {
      currentStep++;
    }
  }

  function prevStep() {
    if (currentStep > 1) {
      currentStep--;
    }
  }

  function goToStep(step) {
    currentStep = step;
  }

  function goHome() {
    // Reset all form data
    formData = {
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      customerNIT: '',
      selectedParent: null,
      selectedChildren: [],
      totalPrice: 0,
      notes: ''
    };
    childProducts = [];
    parentProducts = [];
    categories = [];
    filteredProducts = [];
    selectedParentData = null;
    orderSubmitted = false;
    submittedOrder = null;
    currentStep = 1;
    
    // Navigate to home page
    window.location.href = '/';
  }

  // Debug function to see all products (for troubleshooting)
  async function debugProducts() {
    try {
      const allProducts = await getAllProducts();
      toasts.add({
        title: 'Debug',
        description: `Se encontraron ${allProducts.length} productos en total. Revisa la consola para más detalles.`,
        duration: 5000,
        placement: 'bottom-right',
        type: 'info',
        theme: 'dark',
        showProgress: true,
      });
    } catch (error) {
      console.error('Debug error:', error);
      toasts.add({
        title: 'Debug Error',
        description: `Error: ${error.message}`,
        duration: 5000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
    }
  }

  // Debug function to test gallery collection access
  async function debugGallery() {
    try {
      const galleryItems = await testGalleryAccess();
      toasts.add({
        title: 'Gallery Debug',
        description: `Se encontraron ${galleryItems.length} imágenes en la galería. Revisa la consola para más detalles.`,
        duration: 5000,
        placement: 'bottom-right',
        type: 'info',
        theme: 'dark',
        showProgress: true,
      });
    } catch (error) {
      console.error('Gallery debug error:', error);
      toasts.add({
        title: 'Gallery Error',
        description: `Error: ${error.message}`,
        duration: 5000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
    }
  }

  // Handle child product selection
  function handleChildSelection(childId, isSelected) {
    if (isSelected) {
      if (!formData.selectedChildren.find(child => child.id === childId)) {
        const child = childProducts.find(c => c.id === childId);
        if (child) {
          formData.selectedChildren = [...formData.selectedChildren, {
            id: child.id,
            nombre: child.nombre,
            precio: child.precio || 0,
            cantidad: 1
          }];
        }
      }
    } else {
      formData.selectedChildren = formData.selectedChildren.filter(child => child.id !== childId);
    }
    calculateTotal();
  }

  // Update quantity for selected child
  function updateChildQuantity(childId, quantity) {
    formData.selectedChildren = formData.selectedChildren.map(child => 
      child.id === childId ? { ...child, cantidad: Math.max(1, parseInt(quantity) || 1) } : child
    );
    calculateTotal();
  }

  // Calculate total price
  function calculateTotal() {
    formData.totalPrice = formData.selectedChildren.reduce((total, child) => {
      return total + (child.precio * child.cantidad);
    }, 0);
  }

  // Submit form
  async function handleSubmit(event) {
    event.preventDefault();
    
    if (!formData.customerName || !formData.customerEmail || !formData.customerNIT || !formData.selectedParent || formData.selectedChildren.length === 0) {
      toasts.add({
        title: 'Error',
        description: 'Por favor complete todos los campos requeridos y seleccione al menos un componente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
      return;
    }

    submitting = true;
    let emailSent = false;
    try {
      // Create order record
      const orderData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        customerNIT: formData.customerNIT,
        selectedParent: formData.selectedParent,
        selectedChildren: formData.selectedChildren,
        totalPrice: formData.totalPrice,
        notes: formData.notes
      };

      // Save order using controller
      const result = await saveAnonymousOrder(orderData);
      
      // Set success state
      orderSubmitted = true;
      submittedOrder = {
        ...orderData,
        cotizaId: result.cotizaCab?.id,
        cotizaNumero: result.cotizaCab?.numero
      };
      
      // Move to success step first
      currentStep = 5;
      
      // Send email to customer with PDF (async, don't block UI)
      sendCheckoutEmailToCustomer({
        ...orderData,
        cotiza_id: result.cotizaCab?.id,
        cotiza_numero: result.cotizaCab?.numero,
        cotizaCab: result.cotizaCab,
        cotizaDet: result.cotizaDet
      }).then(() => {
        emailSent = true;
        toasts.add({
          title: 'Email Enviado',
          description: 'Se ha enviado la cotización por correo electrónico',
          duration: 5000,
          placement: 'bottom-right',
          type: 'success',
          theme: 'dark',
          showProgress: true,
        });
      }).catch((emailError) => {
        console.error('Error sending email:', emailError);
        // Don't fail the whole process if email fails
        toasts.add({
          title: 'Advertencia',
          description: 'El pedido se guardó correctamente, pero hubo un problema al enviar el email. Nos pondremos en contacto pronto.',
          duration: 5000,
          placement: 'bottom-right',
          type: 'warning',
          theme: 'dark',
          showProgress: true,
        });
      });
      
      toasts.add({
        title: 'Éxito',
        description: 'Su solicitud ha sido enviada correctamente. Recibirá un email de confirmación en breve.',
        duration: 5000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true,
      });

    } catch (error) {
      console.error('Error submitting order:', error);
      toasts.add({
        title: 'Error',
        description: 'Ocurrió un error al enviar la solicitud',
        duration: 3000,
        placement: 'bottom-right',
        type: 'error',
        theme: 'dark',
        showProgress: true,
      });
    } finally {
      submitting = false;
    }
  }

  // Send email to customer from checkout
  async function sendCheckoutEmailToCustomer(orderData) {
    try {
      console.log('📧 Sending checkout email to customer:', orderData.customerEmail);
      
      // Generate PDF for the quotation
      let pdfBase64 = null;
      try {
        const pdfBlob = await generateCheckoutPDF(orderData);
        pdfBase64 = await blobToBase64(pdfBlob);
        console.log('✅ PDF generated successfully for customer email');
      } catch (pdfError) {
        console.error('⚠️ Error generating PDF for customer email:', pdfError);
        throw new Error('No se pudo generar el PDF');
      }
      
      const requestBody = {
        quotation: {
          numero: orderData.cotiza_numero || 'N/A',
          fecha: new Date().toISOString().split('T')[0],
          nombre: orderData.customerName,
          nit: orderData.customerNIT,
          telefono: orderData.customerPhone,
          correo: orderData.customerEmail,
          total: orderData.totalPrice
        },
        clientEmail: orderData.customerEmail,
        pdfBase64: pdfBase64 || ''
      };
      
      const response = await fetch(`${EMAIL_API_URL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to send customer email:', response.status, errorText);
        throw new Error(`Error al enviar email: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ Customer email sent successfully:', result.messageId);
      return result;
      
    } catch (emailError) {
      console.error('❌ Error sending customer email:', emailError);
      throw emailError;
    }
  }

  // Generate PDF for checkout order
  async function generateCheckoutPDF(orderData) {
    try {
      const { default: jsPDF } = await import('jspdf');
      await import('jspdf-autotable');
      const { 
        addCoverPage,
        addHeader, 
        addQuotationInfo, 
        addClientInfo, 
        addDetailsTable,
        addImages,
        addFooter 
      } = await import('../lib/pdfGenerator.js');
      const { getProductImages } = await import('../controllers/anonymousCheckoutController.js');
      
      // Create new PDF document
      const doc = new jsPDF('p', 'mm', 'a4');
      
      // Set up colors (Nonosky palette: black background with red accents)
      const colors = {
        primary: '#DC2626', // Red-600
        secondary: '#7F1D1D', // Red-800
        background: '#000000', // Black
        text: '#FFFFFF', // White
        textSecondary: '#D1D5DB' // Gray-300
      };

      // Add cover page first
      await addCoverPage(doc, colors);
      
      // Add new page for content
      doc.addPage();

      // Add background
      doc.setFillColor(colors.background);
      doc.rect(0, 0, 210, 297, 'F');

      // Add header with company branding
      await addHeader(doc, colors);
      
      // Prepare quotation object for PDF
      const quotation = {
        numero: orderData.cotiza_numero || 'N/A',
        fecha: new Date().toISOString().split('T')[0],
        nombre: orderData.customerName,
        nit: orderData.customerNIT,
        telefono: orderData.customerPhone,
        correo: orderData.customerEmail,
        total: orderData.totalPrice
      };
      
      // Prepare details array from order data
      const details = [];
      
      // Fetch parent product name if available
      let parentName = 'Servidor Base';
      if (orderData.selectedParent) {
        try {
          const pb = (await import('../lib/pocketbase')).default;
          const parentProduct = await pb.collection('producto').getOne(orderData.selectedParent);
          parentName = parentProduct.nombre || 'Servidor Base';
        } catch (error) {
          console.warn('Could not fetch parent product name:', error);
        }
      }
      
      // Add parent product if available
      if (orderData.selectedParent) {
        details.push({
          expand: { producto: { nombre: parentName } },
          cantidad: 1,
          precio: 0,
          total: 0
        });
      }
      
      // Add child products with their names
      if (orderData.selectedChildren && orderData.selectedChildren.length > 0) {
        for (const child of orderData.selectedChildren) {
          details.push({
            expand: { producto: { nombre: child.nombre || 'Componente' } },
            cantidad: child.cantidad || 1,
            precio: child.precio || 0,
            total: (child.precio || 0) * (child.cantidad || 1)
          });
        }
      }
      
      // Add quotation information
      addQuotationInfo(doc, quotation, colors, details);
      
      // Add client information
      addClientInfo(doc, quotation, colors);
      
      // Add details table
      addDetailsTable(doc, details, colors);
      
      // Collect images from all products (parent + children)
      const allImages = [];
      
      // Get images from parent product
      if (orderData.selectedParent) {
        try {
          const parentImages = await getProductImages(orderData.selectedParent);
          parentImages.forEach(img => allImages.push(img.url));
        } catch (error) {
          console.warn('Could not fetch parent product images:', error);
        }
      }
      
      // Get images from child products
      if (orderData.selectedChildren && orderData.selectedChildren.length > 0) {
        for (const child of orderData.selectedChildren) {
          if (child.id) {
            try {
              const childImages = await getProductImages(child.id);
              childImages.forEach(img => allImages.push(img.url));
            } catch (error) {
              console.warn(`Could not fetch images for child product ${child.id}:`, error);
            }
          }
        }
      }
      
      // Add images section (always add, even if empty - function handles empty arrays)
      console.log('🖼️ Adding images to checkout PDF:', allImages.length, 'images');
      await addImages(doc, allImages, colors);
      
      // Add footer
      addFooter(doc, colors);
      
      // Return PDF as blob
      return doc.output('blob');
      
    } catch (error) {
      console.error('Error generating checkout PDF:', error);
      throw error;
    }
  }

  // Helper function to convert blob to base64
  function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:application/pdf;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
</script>

<div class="min-h-screen bg-black">
  <!-- Header -->
  <div class="bg-gradient-to-r from-red-900 to-red-800 py-6">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-center">
        <div class="text-center">
          <div class="mb-4">
            <div class="inline-block p-4 bg-white rounded-full shadow-lg transform hover:scale-105 transition-transform duration-300">
              <img 
                src="/assets/images/logo_nonosky.svg" 
                alt="NONOSKY" 
                class="h-16 w-auto object-contain"
                on:error={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
              <div class="hidden text-center">
                <div class="text-3xl font-bold text-red-600 mb-1">1S</div>
                <div class="text-sm font-semibold text-gray-700">NONOSKY</div>
              </div>
            </div>
          </div>
          <h1 class="text-3xl font-bold text-white mb-2">Configuración de Servidor</h1>
          <p class="text-red-200">Personaliza tu servidor con los mejores componentes</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Progress Steps -->
  <div class="bg-gray-900 py-4">
    <div class="container mx-auto px-4">
      <div class="flex justify-center">
        <div class="flex items-center space-x-4">
          {#each [1, 2, 3, 4] as step}
            <div class="flex items-center">
              <div class="flex items-center justify-center w-10 h-10 rounded-full border-2 {currentStep >= step ? 'bg-red-600 border-red-600 text-white' : 'border-gray-600 text-gray-400'} transition-all duration-300">
                {step}
              </div>
              {#if step < 4}
                <div class="w-8 h-0.5 mx-2 {currentStep > step ? 'bg-red-600' : 'bg-gray-600'}"></div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
      <div class="flex justify-center mt-2">
        <div class="flex space-x-20 text-sm text-gray-400">
          <span class="{currentStep >= 1 ? 'text-red-400' : ''}">Información</span>
          <span class="{currentStep >= 2 ? 'text-red-400' : ''}">Servidor</span>
          <span class="{currentStep >= 3 ? 'text-red-400' : ''}">Componentes</span>
          <span class="{currentStep >= 4 ? 'text-red-400' : ''}">Revisar</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content -->
  <div class="container mx-auto px-4 py-8">
    <div class="max-w-6xl mx-auto">

      <!-- Step 1: Customer Information -->
      {#if currentStep === 1}
        <div class="bg-gray-900 rounded-lg p-8 border border-red-600">
          <h2 class="text-2xl font-bold text-white mb-6 text-center">Información del Cliente</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-white text-sm font-bold mb-2">Nombre Completo *</label>
              <input
                type="text"
                bind:value={formData.customerName}
                required
                class="w-full px-4 py-3 bg-red-800 text-white rounded-lg border-0 focus:ring-2 focus:ring-red-500 transition-all duration-200 placeholder:text-white"
                placeholder="Ingrese su nombre completo"
              />
            </div>
            <div>
              <label class="block text-white text-sm font-bold mb-2">Email *</label>
              <input
                type="email"
                bind:value={formData.customerEmail}
                required
                class="w-full px-4 py-3 bg-red-800 text-white rounded-lg border-0 focus:ring-2 focus:ring-red-500 transition-all duration-200 placeholder:text-white"
                placeholder="su@email.com"
              />
            </div>
            <div>
              <label class="block text-white text-sm font-bold mb-2">Teléfono</label>
              <input
                type="tel"
                bind:value={formData.customerPhone}
                class="w-full px-4 py-3 bg-red-800 text-white rounded-lg border-0 focus:ring-2 focus:ring-red-500 transition-all duration-200 placeholder:text-white"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label class="block text-white text-sm font-bold mb-2">NIT *</label>
              <input
                type="text"
                bind:value={formData.customerNIT}
                required
                class="w-full px-4 py-3 bg-red-800 text-white rounded-lg border-0 focus:ring-2 focus:ring-red-500 transition-all duration-200 placeholder:text-white"
                placeholder="12345678-9"
              />
            </div>
          </div>
          <div class="flex justify-end mt-8">
            <button
              on:click={nextStep}
              disabled={!formData.customerName || !formData.customerEmail || !formData.customerNIT}
              class="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continuar →
            </button>
          </div>
        </div>
      {/if}

      <!-- Step 2: Server Selection -->
      {#if currentStep === 2}
        <div class="bg-gray-900 rounded-lg p-8 border border-red-600">
          <h2 class="text-2xl font-bold text-white mb-6 text-center">Selecciona tu Servidor Base</h2>
          <p class="text-gray-300 text-center mb-8">Elige el servidor que mejor se adapte a tus necesidades</p>
          
          {#if loading}
            <div class="text-center py-12">
              <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
              <p class="text-gray-300 mt-4">Cargando servidores...</p>
            </div>
          {:else if parentProducts.length > 0}
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {#each parentProducts as product}
                <div class="bg-gray-800 rounded-lg p-6 border border-gray-600 hover:border-red-500 transition-all duration-300 cursor-pointer group"
                     on:click={() => selectParent(product)}
                     on:keydown={(e) => e.key === 'Enter' && selectParent(product)}
                     role="button"
                     tabindex="0">
                  <div class="text-center">
                    <div class="w-16 h-16 bg-red-600 rounded-full mx-auto mb-4 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
                      </svg>
                    </div>
                    <h3 class="text-lg font-bold text-white mb-2">{product.nombre}</h3>
                    <div class="text-gray-300 text-sm mb-4 leading-relaxed text-left">
                      {@html product.descrip || product.descripcion || 'Servidor de alto rendimiento'}
                    </div>
                    {#if product.precio}
                      <p class="text-red-400 font-bold text-xl">${product.precio}</p>
                    {/if}
                    <div class="mt-4">
                      <span class="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold group-hover:bg-red-700 transition-colors duration-200">
                        Seleccionar
                      </span>
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="text-center py-12">
              <div class="text-gray-400 text-lg mb-4">No se encontraron servidores disponibles</div>
              <div class="text-gray-500 text-sm mb-6">
                <p>Esto puede deberse a:</p>
                <ul class="text-left max-w-md mx-auto mt-2">
                  <li>• No hay productos marcados como servidores (sin parent)</li>
                  <li>• Los productos están desactivados (sta = false)</li>
                  <li>• Problema de conexión con la base de datos</li>
                </ul>
              </div>
              <div class="flex space-x-4 justify-center">
                <button
                  on:click={loadParentProducts}
                  class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200"
                >
                  Reintentar
                </button>
                <button
                  on:click={debugProducts}
                  class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200"
                >
                  Debug Productos
                </button>
                <button
                  on:click={debugGallery}
                  class="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 ml-2"
                >
                  Debug Gallery
                </button>
              </div>
            </div>
          {/if}
          
          <div class="flex justify-between mt-8">
            <button
              on:click={prevStep}
              class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200"
            >
              ← Volver
            </button>
          </div>
        </div>
      {/if}

      <!-- Step 3: Component Selection -->
      {#if currentStep === 3}
        <div class="bg-gray-900 rounded-lg p-8 border border-red-600">
          <!-- Header with Cart -->
          <div class="flex items-center justify-between mb-8">
            <div>
              <h2 class="text-3xl font-bold text-white mb-2">Personaliza tu Servidor</h2>
              <p class="text-gray-300">Servidor seleccionado: <span class="text-red-400 font-semibold text-lg">{selectedParentData?.nombre}</span></p>
            </div>
            <button
              on:click={() => showCart = !showCart}
              class="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 relative shadow-lg hover:shadow-red-500/25"
            >
              <div class="flex items-center space-x-2">
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"></path>
                </svg>
                <span>Carrito ({formData.selectedChildren.length})</span>
              </div>
              {#if formData.totalPrice > 0}
                <span class="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center animate-pulse">
                  ${formData.totalPrice.toFixed(0)}
                </span>
              {/if}
            </button>
          </div>

          {#if loading}
            <div class="text-center py-16">
              <div class="inline-block animate-spin rounded-full h-16 w-16 border-4 border-red-600 border-t-transparent"></div>
              <p class="text-gray-300 mt-6 text-lg">Cargando componentes y categorías...</p>
            </div>
          {:else if childProducts.length > 0}
            <!-- Category Navigation -->
            <div class="mb-8">
              <div class="flex flex-wrap gap-3 mb-6">
                <button
                  on:click={() => filterByCategory(null)}
                  class="px-6 py-3 rounded-full font-semibold transition-all duration-300 {selectedCategory === null ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}"
                >
                  <div class="flex items-center space-x-2">
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1V8z" clip-rule="evenodd"></path>
                    </svg>
                    <span>Todos</span>
                  </div>
                </button>
                
                {#each categories as category}
                  <button
                    on:click={() => filterByCategory(category.id)}
                    class="px-6 py-3 rounded-full font-semibold transition-all duration-300 {selectedCategory === category.id ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}"
                  >
                    <div class="flex items-center space-x-2">
                      <div class="w-3 h-3 rounded-full bg-red-400"></div>
                      <span>{category.descrip}</span>
                    </div>
                  </button>
                {/each}
              </div>
              
              <div class="text-sm text-gray-400">
                Mostrando {filteredProducts.length} de {childProducts.length} componentes
                {#if selectedCategory}
                  en la categoría seleccionada
                {/if}
              </div>
            </div>

            <!-- Product Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {#each filteredProducts as child}
                <div class="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-600 hover:border-red-500 transition-all duration-300 group hover:shadow-xl hover:shadow-red-500/10">
                  <!-- Product Image -->
                  <div class="relative mb-4">
                    {#if child.firstImage}
                      <img 
                        src={child.firstImage.url} 
                        alt={child.nombre}
                        class="w-full h-32 object-cover rounded-lg shadow-lg"
                        on:error={(e) => {
                          console.log('❌ Image failed to load:', child.firstImage.url);
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    {/if}
                    <div class="w-full h-32 bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-lg flex items-center justify-center {child.firstImage ? 'hidden' : 'flex'} shadow-lg">
                      <div class="text-center">
                        <svg class="w-16 h-16 text-white opacity-70 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd"></path>
                        </svg>
                        <div class="text-white text-xs opacity-60 font-medium">Imagen no disponible</div>
                      </div>
                    </div>
                    
                    <!-- Selection Checkbox -->
                    <div class="absolute top-2 right-2">
                      <input
                        type="checkbox"
                        id="child_{child.id}"
                        checked={formData.selectedChildren.find(c => c.id === child.id) ? true : false}
                        on:change={(e) => handleChildSelection(child.id, e.target.checked)}
                        class="w-6 h-6 text-red-600 bg-gray-800 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
                      />
                    </div>
                  </div>
                  
                  <!-- Product Info -->
                  <div class="mb-4">
                    <h3 class="text-white font-bold text-lg mb-2 group-hover:text-red-400 transition-colors duration-300">
                      {child.nombre}
                    </h3>
                    <div class="text-gray-300 text-sm leading-relaxed mb-3 text-left">
                      {@html child.descrip || child.descripcion || 'Componente de alta calidad para servidores empresariales'}
                    </div>
                    <div class="flex items-center justify-between">
                      <span class="text-red-400 font-bold text-xl">${child.precio || 0}</span>
                      {#if child.images && child.images.length > 1}
                        <span class="text-gray-400 text-xs">
                          +{child.images.length - 1} imágenes
                        </span>
                      {/if}
                    </div>
                  </div>
                  
                  <!-- Quantity Controls (if selected) -->
                  {#if formData.selectedChildren.find(c => c.id === child.id)}
                    <div class="mt-4 p-4 bg-gradient-to-r from-red-900/30 to-red-800/30 rounded-lg border border-red-600/30">
                      <label class="block text-white text-sm font-bold mb-3">Cantidad</label>
                      <div class="flex items-center space-x-3">
                        <button
                          on:click={() => updateChildQuantity(child.id, Math.max(1, (formData.selectedChildren.find(c => c.id === child.id)?.cantidad || 1) - 1))}
                          class="bg-red-600 hover:bg-red-700 text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                        >
                          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"></path>
                          </svg>
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={formData.selectedChildren.find(c => c.id === child.id)?.cantidad || 1}
                          on:input={(e) => updateChildQuantity(child.id, e.target.value)}
                          class="w-20 px-3 py-2 bg-red-800 text-white rounded-lg text-center font-semibold focus:ring-2 focus:ring-red-500 focus:outline-none placeholder:text-white"
                        />
                        <button
                          on:click={() => updateChildQuantity(child.id, (formData.selectedChildren.find(c => c.id === child.id)?.cantidad || 1) + 1)}
                          class="bg-red-600 hover:bg-red-700 text-white w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                        >
                          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  {/if}
                </div>
              {/each}
            </div>
          {:else}
            <div class="text-center py-16">
              <div class="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg class="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm8 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1V8z" clip-rule="evenodd"></path>
                </svg>
              </div>
              <div class="text-gray-400 text-xl mb-2">No hay componentes disponibles</div>
              <div class="text-gray-500 text-sm">No se encontraron componentes para este servidor</div>
            </div>
          {/if}

          <!-- Navigation Buttons -->
          <div class="flex justify-between mt-12">
            <button
              on:click={() => { currentStep = 2; selectedParentData = null; formData.selectedParent = null; childProducts = []; categories = []; filteredProducts = []; selectedCategory = null; formData.selectedChildren = []; formData.totalPrice = 0; }}
              class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 flex items-center space-x-2"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"></path>
              </svg>
              <span>Cambiar Servidor</span>
            </button>
            <button
              on:click={nextStep}
              disabled={formData.selectedChildren.length === 0}
              class="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg hover:shadow-red-500/25"
            >
              <span>Revisar Pedido</span>
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path>
              </svg>
            </button>
          </div>
        </div>
      {/if}

      <!-- Step 4: Review & Submit -->
      {#if currentStep === 4}
        <div class="bg-gray-900 rounded-lg p-8 border border-red-600">
          <h2 class="text-2xl font-bold text-white mb-6 text-center">Revisa tu Pedido</h2>
          
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Customer Info -->
            <div>
              <h3 class="text-lg font-bold text-white mb-4">Información del Cliente</h3>
              <div class="bg-gray-800 rounded-lg p-4">
                <p class="text-white"><span class="text-gray-400">Nombre:</span> {formData.customerName}</p>
                <p class="text-white"><span class="text-gray-400">Email:</span> {formData.customerEmail}</p>
                {#if formData.customerPhone}
                  <p class="text-white"><span class="text-gray-400">Teléfono:</span> {formData.customerPhone}</p>
                {/if}
                <p class="text-white"><span class="text-gray-400">NIT:</span> {formData.customerNIT}</p>
              </div>
            </div>

            <!-- Order Summary -->
            <div>
              <h3 class="text-lg font-bold text-white mb-4">Resumen del Pedido</h3>
              <div class="bg-gray-800 rounded-lg p-4">
                <div class="mb-4">
                  <p class="text-white font-semibold">Servidor: {selectedParentData?.nombre}</p>
                  {#if selectedParentData?.precio}
                    <p class="text-red-400">${selectedParentData.precio}</p>
                  {/if}
                </div>
                
                {#if formData.selectedChildren.length > 0}
                  <div class="border-t border-gray-600 pt-4">
                    <p class="text-white font-semibold mb-2">Componentes:</p>
                    {#each formData.selectedChildren as child}
                      <div class="flex justify-between items-center py-1">
                        <span class="text-gray-300">{child.nombre} x{child.cantidad}</span>
                        <span class="text-red-400">${(child.precio * child.cantidad).toFixed(2)}</span>
                      </div>
                    {/each}
                  </div>
                  
                  <div class="border-t border-red-600 pt-4 mt-4">
                    <div class="flex justify-between items-center">
                      <span class="text-white font-bold text-lg">Total:</span>
                      <span class="text-red-400 font-bold text-xl">${formData.totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                {/if}
              </div>
            </div>
          </div>

          <!-- Additional Notes -->
          <div class="mt-8">
            <h3 class="text-lg font-bold text-white mb-4">Notas Adicionales</h3>
            <textarea
              bind:value={formData.notes}
              rows="4"
              class="w-full px-4 py-3 bg-red-800 text-white rounded-lg border-0 focus:ring-2 focus:ring-red-500 transition-all duration-200 placeholder:text-white"
              placeholder="Cualquier información adicional sobre tu configuración..."
            ></textarea>
          </div>

          <div class="flex justify-between mt-8">
            <button
              on:click={prevStep}
              class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200"
            >
              ← Volver
            </button>
            <button
              on:click={handleSubmit}
              disabled={submitting}
              class="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {#if submitting}
                <div class="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Enviando...
              {:else}
                Enviar Solicitud
              {/if}
            </button>
          </div>
        </div>
      {/if}

      <!-- Step 5: Success -->
      {#if currentStep === 5}
        <div class="bg-gray-900 rounded-lg p-8 border border-green-600">
          <div class="text-center">
            <!-- Success Icon -->
            <div class="w-20 h-20 bg-green-600 rounded-full mx-auto mb-6 flex items-center justify-center">
              <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h2 class="text-3xl font-bold text-white mb-4">¡Solicitud Enviada Exitosamente!</h2>
            <p class="text-gray-300 text-lg mb-8">Gracias por tu interés. Nos pondremos en contacto contigo pronto.</p>
            
            {#if submittedOrder}
              <div class="bg-gray-800 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                <h3 class="text-xl font-bold text-white mb-4">Resumen de tu Solicitud</h3>
                
                <!-- Customer Info -->
                <div class="mb-6">
                  <h4 class="text-lg font-semibold text-white mb-2">Información de Contacto</h4>
                  <div class="text-gray-300 space-y-1">
                    <p><span class="text-gray-400">Nombre:</span> {submittedOrder.customerName}</p>
                    <p><span class="text-gray-400">Email:</span> {submittedOrder.customerEmail}</p>
                    {#if submittedOrder.customerPhone}
                      <p><span class="text-gray-400">Teléfono:</span> {submittedOrder.customerPhone}</p>
                    {/if}
                    <p><span class="text-gray-400">NIT:</span> {submittedOrder.customerNIT}</p>
                  </div>
                </div>
                
                <!-- Order Details -->
                <div class="mb-6">
                  <h4 class="text-lg font-semibold text-white mb-2">Configuración Solicitada</h4>
                  <div class="text-gray-300">
                    <p class="mb-2"><span class="text-gray-400">Servidor:</span> {selectedParentData?.nombre}</p>
                    <p class="mb-2"><span class="text-gray-400">Componentes:</span> {submittedOrder.selectedChildren.length} seleccionados</p>
                    <p class="text-red-400 font-bold text-xl">Total: ${submittedOrder.totalPrice.toFixed(2)}</p>
                  </div>
                </div>
                
                <!-- Quote Info -->
                {#if submittedOrder.cotizaNumero}
                  <div class="border-t border-gray-600 pt-4">
                    <p class="text-gray-400 text-sm">Número de Cotización: <span class="text-white font-semibold">{submittedOrder.cotizaNumero}</span></p>
                  </div>
                {/if}
              </div>
            {/if}
            
            <!-- Action Buttons -->
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                on:click={goHome}
                class="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200"
              >
                🏠 Volver al Inicio
              </button>
              <button
                on:click={() => {
                  orderSubmitted = false;
                  submittedOrder = null;
                  currentStep = 1;
                }}
                class="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200"
              >
                📝 Nueva Configuración
              </button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>
